import React, { useEffect, useState } from "react";

import _ from "lodash";
import { Moment } from "moment";
import { useTranslation } from "react-i18next";

import { Button, DatePicker, Form, Input, Row, Select, Spin } from "antd";

import { NumberInput } from "../../../../../components/inputs";
import { PlaceSelector } from "../../../../../components/selectors";
import { availableTripClass, TripClass } from "../../../../../store/common_types";

export interface Passengers {
  adultsPassengers?: number;
  childrenPassengers?: number;
  infantsPassengers?: number;
}

export interface FlightsFormErrors {
  origin?: string;
  destination?: string;
  departureDate?: string;
  returnDate?: string;
  adults?: string;
  children?: string;
  infants?: string;
  tripClass?: string;
  locale?: string;
  currency?: string;
  url?: string;
}

export interface FlightsFormProps extends Passengers {
  className?: string;

  origin?: string;
  destination?: string;
  departureDate?: Moment;
  returnDate?: Moment;
  partner?: string;
  password?: string;

  tripClass?: TripClass;
  locale?: string;
  currency?: string;
  url?: string;
  errors?: FlightsFormErrors;
  onFinish?: (value: FlightsFormResult) => void;
  isLoading?: boolean;
}

export interface FlightsFormResult {
  origin: string;
  destination: string;
  departureDate: Moment;
  returnDate?: Moment;

  adultsPassengers: number;
  childrenPassengers: number;
  infantsPassengers: number;

  tripClass: TripClass;
  locale?: string;
  currency?: string;

  partner?: string;
  password?: string;

  url: string;
}

export const FlightsForm: React.FC<FlightsFormProps> = (props) => {
  const { t } = useTranslation("gate_tester_page");
  const maxTotalPassengers = 9;
  const [state, setState] = useState({
    departureDate: props.departureDate,
    current: { adultsPassengers: 1, childrenPassengers: 0, infantsPassengers: 0 },
    max: { adultsPassengers: maxTotalPassengers, childrenPassengers: maxTotalPassengers, infantsPassengers: 1 },
    errors: props.errors ?? {},
  });

  useEffect(() => {
    setState((c) => ({ ...c, errors: props.errors ?? {} }));
  }, [props.errors]);

  const handleValuesChange = (values: {}) => {
    setState((currentState) => {
      if (_.isEmpty(currentState.errors)) {
        return currentState;
      }
      const errors = _.keys(currentState.errors).reduce((current, field) => {
        if (field in values) {
          return current;
        }
        return { ...current, [field]: currentState.errors[field as keyof FlightsFormErrors] };
      }, {});
      return { ...currentState, errors };
    });
  };

  const handleFinish = (value: FlightsFormResult) => {
    if (_.isEmpty(state.errors) && props.onFinish) {
      props.onFinish(value);
    }
  };

  const checkPassengers = ({ field }: any, value: number) => {
    setState((currentState) => {
      const passengers = { ...currentState.current, [field]: value };
      const maxPassengers = {
        adultsPassengers: 9,
        childrenPassengers: 9,
        infantsPassengers: passengers.adultsPassengers,
      };
      const total = (_.keys(currentState.current) as (keyof Passengers)[]).reduce((sum, key) => {
        return sum + (key === field ? value : currentState.current[key]);
      }, 0);
      return { ...currentState, current: passengers, max: total === maxTotalPassengers ? passengers : maxPassengers };
    });
    return Promise.resolve();
  };

  const checkURLHostname = ({ field }: any, value: string) => {
    try {
      const url = new URL(value);
      if (/.*\.[0-9]+$/.test(url.hostname) || url.hostname === "localhost") {
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject(`'${field}' use IP address as hostname`);
      }
    } catch {
      return Promise.resolve();
    }
    return Promise.resolve();
  };

  const handleChangeDepartureDate = (value: Moment | null, dateStr: string) => {
    setState((currentState) => ({ ...currentState, departureDate: value ?? undefined }));
  };
  const disabledReturnDate = (current: Moment): boolean => {
    return state.departureDate ? current.isBefore(state.departureDate) : false;
  };

  return (
    <Spin spinning={props.isLoading ?? false}>
      <Form
        name="flights"
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
        className={props.className}
      >
        <Form.Item
          label={t("forms.flights.origin.label")}
          name="origin"
          validateTrigger={["onFocus", "onChange"]}
          rules={[{ required: true, message: t("forms.flights.origin.message") }]}
          initialValue={props.origin}
          validateStatus={state.errors.origin ? "error" : undefined}
          help={state.errors.origin}
        >
          <PlaceSelector showArrow={false} />
        </Form.Item>
        <Form.Item
          label={t("forms.flights.destination.label")}
          name="destination"
          validateTrigger={["onFocus", "onChange"]}
          rules={[{ required: true, message: t("forms.flights.destination.message") }]}
          initialValue={props.destination}
          validateStatus={state.errors.destination ? "error" : undefined}
          help={state.errors.destination}
        >
          <PlaceSelector showArrow={false} />
        </Form.Item>
        <Row>
          <Form.Item
            label={t("forms.flights.departureDate.label")}
            name="departureDate"
            validateTrigger={["onFocus", "onChange"]}
            initialValue={props.departureDate}
            rules={[{ required: true, message: t("forms.flights.departureDate.message") }]}
            style={{ width: "calc(50% - 5px)" }}
            validateStatus={state.errors.departureDate ? "error" : undefined}
            help={state.errors.departureDate}
          >
            <DatePicker style={{ width: "100%" }} onChange={handleChangeDepartureDate} />
          </Form.Item>
          <Form.Item
            label={t("forms.flights.returnDate.label")}
            name="returnDate"
            tooltip={t("forms.flights.returnDate.help")}
            style={{ width: "calc(50% - 5px)" }}
            validateStatus={state.errors.returnDate ? "error" : undefined}
            help={state.errors.returnDate}
          >
            <DatePicker style={{ width: "100%" }} disabledDate={disabledReturnDate} />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item
            label={t("forms.flights.passengers.adults.label")}
            name="adultsPassengers"
            tooltip={t("forms.flights.passengers.adults.help")}
            validateTrigger={["onFocus", "onChange"]}
            initialValue={state.current.adultsPassengers}
            rules={[
              { required: true, message: t("forms.flights.passengers.adults.message"), validator: checkPassengers },
            ]}
            validateStatus={state.errors.adults ? "error" : undefined}
            help={state.errors.adults}
          >
            <NumberInput min={1} max={state.max.adultsPassengers} />
          </Form.Item>
          <Form.Item
            label={t("forms.flights.passengers.children.label")}
            name="childrenPassengers"
            tooltip={t("forms.flights.passengers.children.help")}
            rules={[{ validator: checkPassengers }]}
            initialValue={state.current.childrenPassengers}
            validateStatus={state.errors.children ? "error" : undefined}
            help={state.errors.children}
          >
            <NumberInput min={0} max={state.max.childrenPassengers} />
          </Form.Item>
          <Form.Item
            label={t("forms.flights.passengers.infants.label")}
            name="infantsPassengers"
            tooltip={t("forms.flights.passengers.infants.help")}
            rules={[{ validator: checkPassengers }]}
            initialValue={state.current.infantsPassengers}
            validateStatus={state.errors.infants ? "error" : undefined}
            help={state.errors.infants}
          >
            <NumberInput
              min={0}
              max={state.max.infantsPassengers}
              incrementDisabledMessage={t("forms.flights.passengers.infants.incrementDisabledMessage")}
            />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item
            label={t("forms.flights.tripClass.label")}
            name="tripClass"
            rules={[{ required: true, message: t("forms.flights.tripClass.message") }]}
            initialValue={props.tripClass ?? "Y"}
            style={{ width: "calc(40% - 5px)" }}
            validateStatus={state.errors.tripClass ? "error" : undefined}
            help={state.errors.tripClass}
          >
            <Select>
              {availableTripClass.map((tripClass) => (
                <Select.Option key={`trip-class-${tripClass}`} value={tripClass}>
                  {t(`forms.flights.tripClass.options.${tripClass}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("forms.flights.locale.label")}
            name="locale"
            tooltip={t("forms.flights.locale.help")}
            rules={[{ type: "string", len: 2, message: t("forms.flights.locale.message") }]}
            style={{ width: "calc(30% - 5px)" }}
            initialValue={props.locale}
            validateStatus={state.errors.locale ? "error" : undefined}
            help={state.errors.locale}
          >
            <Input
              style={{ textTransform: "lowercase" }}
              placeholder={t("forms.flights.locale.placeholder")}
              autoComplete={"off"}
              maxLength={2}
            />
          </Form.Item>
          <Form.Item
            label={t("forms.flights.currency.label")}
            name="currency"
            tooltip={t("forms.flights.currency.help")}
            rules={[{ type: "string", len: 3, message: t("forms.flights.currency.message") }]}
            style={{ width: "calc(30% - 5px)" }}
            initialValue={props.currency}
            validateStatus={state.errors.currency ? "error" : undefined}
            help={state.errors.currency}
          >
            <Input
              style={{ textTransform: "uppercase" }}
              placeholder={t("forms.flights.currency.placeholder")}
              autoComplete={"off"}
              maxLength={3}
            />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item label={t("forms.flights.credential.partner.label")} name="partner" initialValue={props.partner}>
            <Input placeholder={t("forms.flights.credential.partner.placeholder")} autoComplete={"off"} />
          </Form.Item>
          <Form.Item label={t("forms.flights.credential.password.label")} name="password" initialValue={props.password}>
            <Input.Password placeholder={t("forms.flights.credential.password.placeholder")} autoComplete={"off"} />
          </Form.Item>
        </Row>
        <Form.Item
          label={t("forms.flights.url.label")}
          name="url"
          validateTrigger={["onFocus", "onChange"]}
          rules={[
            { required: true, message: t("forms.flights.url.messages.required") },
            { type: "url", message: t("forms.flights.url.messages.invalid") },
            { message: t("forms.flights.url.messages.invalidHostname"), validator: checkURLHostname },
          ]}
          initialValue={props.url}
          validateStatus={state.errors.url ? "error" : undefined}
          help={state.errors.url}
        >
          <Input placeholder={t("forms.flights.url.placeholder")} autoComplete={"off"} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t("forms.flights.submit")}
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};
